<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      loading: true,
      documents: [],
      search: '',
      typeFilter: '',
      typeOptions: ['offer_letter', 'appointment_letter', 'warning_letter', 'experience_letter', 'other'],
      typeLabels: {
        offer_letter: 'Offer Letter',
        appointment_letter: 'Appointment Letter',
        warning_letter: 'Warning Letter',
        experience_letter: 'Experience Letter',
        other: 'Other',
      },
      deleteItem: null,
    };
  },
  computed: {
    filteredDocuments() {
      let list = this.documents;
      if (this.typeFilter) {
        list = list.filter((d) => d.type === this.typeFilter);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter((d) => (d.name || '').toLowerCase().includes(q));
      }
      return list;
    },
  },
  mounted() {
    this.fetchDocuments();
  },
  methods: {
    async fetchDocuments() {
      this.loading = true;
      try {
        const response = await axios.get('/hrms/hrms-documents');
        this.documents = response.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    typeLabel(t) {
      return this.typeLabels[t] || t || '-';
    },

    typeClass(type) {
      const map = {
        offer_letter: 'doc-tag doc-offer',
        appointment_letter: 'doc-tag doc-appointment',
        warning_letter: 'doc-tag doc-warning',
        experience_letter: 'doc-tag doc-experience',
        other: 'doc-tag doc-general',
      };
      return map[type] || 'doc-tag doc-general';
    },

    // ----- create / edit template -----
    goAdd() {
      this.$refs.docModal.create();
      this.$openModal('hrm_document_modal');
    },
    goEdit(row) {
      this.$refs.docModal.edit(row);
      this.$openModal('hrm_document_modal');
    },
    onSaved() {
      this.fetchDocuments();
    },

    // ----- assign to employee -----
    openAssign(row) {
      this.$refs.assignModal.open(row);
      this.$openModal('hrm_document_assign_modal');
    },

    // ----- delete template -----
    confirmDelete(row) {
      this.deleteItem = row;
      this.$openModal('hrm_document_delete_modal');
    },
    async deleteDocument() {
      this.setLoading(true);
      try {
        const res = await axios.delete(`/hrms/hrms-documents/${this.deleteItem.id}`);
        this.$swalToast(res.data.message);
        this.$closeModal('hrm_document_delete_modal');
        this.fetchDocuments();
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-flex align-items-center justify-content-between page-breadcrumb mb-3">
        <div>
          <h4 class="page-title mb-0">Document Templates</h4>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="goAdd">
            <i class="ti ti-circle-plus me-2"></i>Add Template
          </a>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
        <div class="card-body d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Templates
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ filteredDocuments.length }})</span>
          </h5>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <select class="form-select form-select-sm" style="width:auto; min-width:150px;" v-model="typeFilter">
              <option value="">All Types</option>
              <option v-for="t in typeOptions" :key="t" :value="t">{{ typeLabel(t) }}</option>
            </select>

            <div class="l-search-wrap">
              <input type="text" class="l-search-input" v-model="search" placeholder="Search templates..." />
              <span style="padding:0 10px; color:#94a3b8;"><i class="ti ti-search"></i></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="doc-state">
        <div class="spinner-border text-primary mb-2"></div>
        <div>Loading templates...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="!filteredDocuments.length" class="doc-state">
        <i class="ti ti-file-description d-block mb-2" style="font-size:38px; opacity:.35;"></i>
        No document templates found.
      </div>

      <!-- Card Grid -->
      <div v-else class="row g-3">
        <div v-for="doc in filteredDocuments" :key="doc.id" class="col-xxl-4 col-lg-6 col-md-6">
          <div class="doc-card">

            <div class="doc-card-top">
              <span :class="typeClass(doc.type)">{{ typeLabel(doc.type) }}</span>
              <div class="dropdown">
                <button class="btn btn-icon btn-sm rounded-circle" type="button" data-bs-toggle="dropdown">
                  <i class="ti ti-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="goEdit(doc)">
                      <i class="ti ti-edit me-1"></i>Edit
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item rounded-1" href="javascript:void(0);" @click="openAssign(doc)">
                      <i class="ti ti-user-plus me-1"></i>Assign to Employee
                    </a>
                  </li>
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a class="dropdown-item rounded-1 text-danger" href="javascript:void(0);" @click="confirmDelete(doc)">
                      <i class="ti ti-trash me-1"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="doc-card-body" @click="goEdit(doc)">
              <div class="doc-icon"><i class="ti ti-file-text"></i></div>
              <div class="doc-name-wrap">
                <h6 class="doc-name">{{ doc.name }}</h6>
                <span class="doc-updated">Updated {{ $formatDate(doc.updated_at) }}</span>
              </div>
            </div>

            <div class="doc-card-foot">
              <button class="btn btn-sm btn-light flex-fill" @click="goEdit(doc)">
                <i class="ti ti-edit me-1"></i>Edit
              </button>
              <button class="btn btn-sm btn-primary flex-fill" @click="openAssign(doc)">
                <i class="ti ti-user-plus me-1"></i>Assign
              </button>
            </div>

          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Create / Edit Modal -->
  <hrm-document-modal ref="docModal" @updated="onSaved"></hrm-document-modal>

  <!-- Assign Modal -->
  <hrm-document-assign-modal ref="assignModal"></hrm-document-assign-modal>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_document_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">Delete <strong>{{ deleteItem?.name }}</strong>? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteDocument">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.l-search-wrap {
  display: flex; align-items: center;
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  overflow: hidden; background: #fff;
}
.l-search-wrap:focus-within { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.1); }
.l-search-input {
  border: none; outline: none; height: 34px; padding: 0 10px;
  font-size: 12.5px; color: #1e293b; width: 200px; background: transparent;
}

.doc-state {
  text-align: center; padding: 60px 20px;
  color: #94a3b8; font-size: 13.5px;
  background: #fff; border-radius: 14px;
}

.doc-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
  height: 100%;
  transition: all .18s ease;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.doc-card:hover { border-color: #c7d2fe; box-shadow: 0 8px 20px rgba(99,102,241,.1); }

.doc-card-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }

.doc-card-body { display: flex; align-items: center; gap: 12px; cursor: pointer; margin-bottom: 14px; }
.doc-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: #eef2ff; color: #4f46e5;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
}
.doc-name-wrap { min-width: 0; }
.doc-name {
  font-size: 14px; font-weight: 700; color: #1e293b; margin: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.doc-updated { font-size: 11px; color: #94a3b8; }

.doc-card-foot { display: flex; gap: 8px; padding-top: 12px; border-top: 1px solid #f1f5f9; }

.doc-tag {
  display: inline-block; font-size: 10.5px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px;
}
.doc-offer { background: #eef2ff; color: #4f46e5; }
.doc-appointment { background: #e0f2fe; color: #0369a1; }
.doc-warning { background: #fee2e2; color: #dc2626; }
.doc-experience { background: #fef3c7; color: #b45309; }
.doc-general { background: #f1f5f9; color: #475569; }
</style>